import React, { useEffect, useState } from 'react';
import { Loader2, MessageSquareWarning, CheckCircle2, User } from 'lucide-react';
import { studentsApi } from '../../students.api';
import type { StudentConcern, ConcernStatus, ConcernPriority } from '../profile.types';
import { titleCase } from '@/utils/text';

interface Props { uuid: string }

const STATUS_BADGE: Record<ConcernStatus, string> = {
  open: 'b-danger',
  in_progress: 'b-warning',
  resolved: 'b-success',
  closed: 'b-neutral',
};

const PRIORITY_BADGE: Record<ConcernPriority, string> = {
  urgent: 'b-danger',
  high: 'b-warning',
  normal: 'b-neutral',
  low: 'b-neutral',
};

const ConcernsTab: React.FC<Props> = ({ uuid }) => {
  const [concerns, setConcerns] = useState<StudentConcern[] | null>(null);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    studentsApi.concerns(uuid)
      .then((r) => {
        if (r.success && r.data) {
          setConcerns(r.data.concerns);
          setOpenCount(r.data.open);
        } else setConcerns([]);
      })
      .catch(() => setConcerns([]))
      .finally(() => setLoading(false));
  }, [uuid]);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--slate)' }} /></div>;
  }

  if (!concerns || concerns.length === 0) {
    return (
      <div className="empty">
        <div className="empty-art"><CheckCircle2 size={40} /></div>
        <h3>No concerns raised</h3>
        <p>Help-desk concerns raised for this student will appear here.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
        <Stat label="Total raised" value={concerns.length} />
        <Stat label="Open" value={openCount} color={openCount > 0 ? 'var(--danger)' : 'var(--success)'} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {concerns.map((c) => (
          <div key={c.id} className="card card-pad" style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <MessageSquareWarning size={15} style={{ color: 'var(--text-2)' }} />
              <span style={{ fontWeight: 700, fontSize: 14 }}>{c.title}</span>
              <span className={`badge ${STATUS_BADGE[c.status]}`} style={{ marginLeft: 'auto' }}>
                <span className="dot" />{titleCase(c.status.replace('_', ' '))}
              </span>
            </div>

            {c.description && (
              <p style={{ margin: '10px 0 0', fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.5 }}>{c.description}</p>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="chip chip-sm">{titleCase(c.category)}</span>
              <span className="chip chip-sm">{titleCase(c.concern_type.replace('_', ' '))}</span>
              <span className={`badge ${PRIORITY_BADGE[c.priority]}`}><span className="dot" />{titleCase(c.priority)}</span>
              {c.assignee && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-2)' }}>
                  <User size={12} /> {titleCase(c.assignee.name)}
                </span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)' }}>
                {new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>

            {c.admin_response && (
              <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--surface-tint)', borderRadius: 8, fontSize: 13, color: 'var(--text-2)' }}>
                <span style={{ fontWeight: 700, color: 'var(--text)' }}>Response: </span>{c.admin_response}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

function Stat({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.8px', color: color || 'var(--text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

export default ConcernsTab;
